import { Component, OnInit, Inject, OnDestroy, ChangeDetectorRef, HostListener, ViewChild } from '@angular/core';
import { ActivatedRoute, ParamMap } from '@angular/router';
import { L10nLocale, L10N_LOCALE } from 'angular-l10n';
import { Observable, of, Subject } from 'rxjs';
import { map, mergeMap, skip, switchMap, take, takeUntil } from 'rxjs/operators';
import { LinkStatusCode } from '../../enums/link-status-code.enum';
import { Link } from '../../interfaces/link.interface';
import { MarketPlaceItem } from '../../interfaces/market-place-item.interface';
import { SelectedCompanyService } from '../../selected-company/selected-company.service';
import { LinkService } from '../../services/link.service';
import { MarketPlaceItemService } from '../../services/market-place-item.service';
import { LinkEditComponent } from '../link-edit/link-edit.component';
import { LinkHeaderComponent } from '../link-header/link-header.component';
import { NgIf, AsyncPipe } from '@angular/common';
import Swal from 'sweetalert2';
import { LinkGenericMapperApiService } from '../link-generic-mapper/services/link-generic-mapper-api.service';

@Component({
    selector: 'al-link-edit-page',
    templateUrl: './link-edit-page.component.html',
    styleUrls: ['./link-edit-page.component.scss'],
    standalone: true,
    imports: [
        NgIf,
        LinkHeaderComponent,
        LinkEditComponent,
        AsyncPipe,
    ],
})
export class LinkEditPageComponent implements OnInit, OnDestroy {
	@ViewChild('linkEdit')
	linkEdit?: LinkEditComponent;

	/** Observable containing all page data */
	public link$: Observable<Link>;
	public linkInfo$: Observable<MarketPlaceItem[]>;
	public routeQueryParams$: Observable<{ instanceId: string | null; jobLogId: number | null }>;
	public isMapperEnabled = false;

	private destroy$ = new Subject<void>();

	constructor(
		private route: ActivatedRoute,
		@Inject(L10N_LOCALE) public locale: L10nLocale,
		private linkService: LinkService,
		private marketPlaceItemService: MarketPlaceItemService,
		private cdr: ChangeDetectorRef,
		private selectedUserCompanyService: SelectedCompanyService,
		private linkGenericMapperApiService: LinkGenericMapperApiService
	) {}

	ngOnInit(): void {
		this.routeQueryParams$ = this.route.queryParamMap.pipe(
			map(params => ({
				instanceId: params.get('instanceId'),
				jobLogId: Number(params.get('jobLogId')) || null,
			}))
		);

		this.route.paramMap.pipe(take(1)).subscribe((params: ParamMap) => {
			let id = +params.get('id');
			if (id > 0) {
				this.link$ = this.linkService.get(id);
				this.linkInfo$ = this.marketPlaceItemService.getFromLink(id);
				this.refreshMapperAvailability(id);
				// this.linkInfo$.subscribe(e => {
				// });
			} else {
				this.link$ = of({
					companyId: -1,
					name: '',
					description: '',
					expiryDate: null,
					saveLogs: false,
					mailAddress: '',
					mailLogs: true,
					excludeFromExpiryNotificationMail: false,
					toAppLoginId: -1,
					fromAppLoginId: -1,
					fromLinkTemplateId: -1,
					toLinkTemplateId: -1,
					toApplicationLoginId: -1,
					fromApplicationLoginId: -1,
					statusColor: LinkStatusCode.Undefined,
					statusLabel: '',
					hourlySchedulerAllowanceHours: null
				});
				this.route.queryParamMap
					.pipe(
						map(r => r.get('linkInfoId')),
						takeUntil(this.destroy$)
					)
					.subscribe(linkInfoId => this.initLinkInfo(Number(linkInfoId)));
			}
		});
	}

	ngOnDestroy(): void {
		this.destroy$.next();
		this.destroy$.complete();
	}

	@HostListener('window:beforeunload', ['$event'])
	onBeforeUnload(event: BeforeUnloadEvent): void {
		if (!this.linkEdit?.hasUnsavedChanges()) {
			return;
		}

		event.preventDefault();
		event.returnValue = '';
	}

	canDeactivate(): Promise<boolean> {
		return this.linkEdit?.canDeactivate() ?? Promise.resolve(true);
	}

	initLinkInfo(id: number): void {
		this.linkInfo$ = this.marketPlaceItemService.getOne(id);
	}

	refreshMapperAvailability(linkId?: number | null): void {
		if (!linkId || linkId <= 0) {
			this.isMapperEnabled = false;
			return;
		}

		this.linkGenericMapperApiService.getAvailability(linkId)
			.pipe(takeUntil(this.destroy$))
			.subscribe({
				next: result => {
					this.isMapperEnabled = result.enabled === true;
				},
				error: error => {
					this.isMapperEnabled = false;
					if ((error as { status?: number })?.status === 404) {
						return;
					}

					void Swal.fire({
						icon: 'error',
						title: 'Mapper availability could not be checked',
						text: this.getMapperAvailabilityErrorMessage(error),
						confirmButtonText: 'OK'
					});
				}
			});
	}

	private getMapperAvailabilityErrorMessage(error: unknown): string {
		const httpError = error as { status?: number; statusText?: string; error?: unknown; message?: string };
		const responseBody = httpError?.error;

		if (typeof responseBody === 'string' && responseBody.trim()) {
			return responseBody.trim();
		}

		if (responseBody && typeof responseBody === 'object') {
			const applicationErrorMessage = this.getApplicationErrorMessage((responseBody as any).errors);
			if (applicationErrorMessage) {
				return applicationErrorMessage;
			}

			const message = (responseBody as any).message ?? (responseBody as any).detail ?? (responseBody as any).title;
			if (typeof message === 'string' && message.trim()) {
				return message.trim();
			}
		}

		if (httpError?.status) {
			return `The mapper availability endpoint failed (${httpError.status}${httpError.statusText ? ` ${httpError.statusText}` : ''}).`;
		}

		return httpError?.message || 'The mapper availability endpoint failed.';
	}

	private getApplicationErrorMessage(errors: unknown): string | null {
		if (!Array.isArray(errors)) {
			return null;
		}

		const messages = errors
			.map(error => {
				if (typeof error === 'string') {
					return error.trim();
				}

				if (error && typeof error === 'object') {
					return String((error as any).message ?? '').trim();
				}

				return '';
			})
			.filter(Boolean);

		return messages.length ? messages.join('\n') : null;
	}
}
